import { defineMessages } from '../translate';

/**
 * The product glossary as UI strings — the SAME keys and English words as
 * `GLOSSARY` in `shared/lib/glossary.ts` (a spec keeps them equal), plus the
 * derived labels built from them. Each locale's `glossary.ts` is that
 * language's terminology table: translate every other namespace with it.
 */
export default defineMessages('glossary', {
  // Navigation and spaces
  home: 'Home',
  myWork: 'My Work',
  inbox: 'Inbox',
  chat: 'Chat',
  channel: 'Channel',
  directMessages: 'Direct messages',
  people: 'People',
  // My private space (`teams.kind='personal'`) — never called a team: no
  // members, no "Team settings", subtitle "Only you". Also replaces
  // "<Name>'s Projects" (a pattern, not a literal).
  // The default list of Personal (the hidden capture project, key ME,
  // `system_kind='inbox'`): where every capture lands. Never "Inbox" (that is
  // notifications) and never a second "Personal".
  personal: 'Personal',
  personalList: 'Quick tasks',
  onlyYou: 'Only you',
  team: 'Team',
  project: 'Project',
  milestone: 'Milestone',
  teamSettings: 'Team settings',
  docs: 'Docs',
  time: 'Time',
  timesheet: 'Timesheet',
  favorites: 'Favorites',
  ai: 'AI',
  assistant: 'Assistant',
  agents: 'Agents',
  approvals: 'Approvals',
  billingPlan: 'Plan',
  // My Work
  today: 'Today',
  week: 'Week',
  unplanned: 'Unplanned',
  later: 'Later',
  all: 'All',
  plan: 'Plan',
  when: 'When',
  carriedOver: 'Carried over',
  capture: 'Capture',
  addToMyWork: 'Add to My Work',
  removeFromPlan: 'Remove from plan',
  privateNote: 'Private note',
  mySteps: 'My steps',
  linkedTo: 'Linked to',
  externalProject: 'External project',
  sendToTeam: 'Move to a team',
  waitingOnOthers: 'Waiting on others',
  // Calendar / Home › My week layer: deadlines of tasks I created for others or collaborate on.
  waitingAndShared: 'Waiting & shared',
  // Personal tier (Wave 6, §7). "Plan your day" is the morning ritual push;
  // "✨ Plan my day" is the AI planner — two different things, never swapped.
  reminder: 'Reminder',
  remindMe: 'Remind me',
  rituals: 'Rituals',
  planYourDay: 'Plan your day',
  wrapUp: 'Wrap up',
  weeklyReview: 'Weekly review',
  planMyDay: 'Plan my day',
  planMyWeek: 'Plan my week',
  timeBlock: 'Time block',
  // The plan feed ("Plan feed" in docs): the private ICS feed of My plan.
  planFeed: 'My plan in your calendar',
  // Task fields and metrics (definitions: packages/shared/src/metrics)
  deadline: 'Deadline',
  overdue: 'Overdue',
  dueSoon: 'Due soon',
  atRisk: 'At risk',
  blocked: 'Blocked',
  toReview: 'To review',
  // Status categories (sentence case, like every other label).
  statusOpen: 'Open',
  statusInProgress: 'In progress',
  statusReview: 'Review',
  statusDone: 'Done',
  statusCancelled: 'Cancelled',
  // Feeds: my notifications vs a team / project audit trail — two different things.
  latestInInbox: 'Latest in Inbox',
  recentActivity: 'Recent activity',
  // Task roles
  author: 'Author',
  respondent: 'Respondent',
  assignee: 'Assignee',
  collaborator: 'Collaborator',
  watcher: 'Watcher',
  // Team roles (labels only; the enum stays OWNER…VIEWER)
  owner: 'Owner',
  manager: 'Manager',
  lead: 'Lead',
  member: 'Member',
  contractor: 'Contractor',
  guest: 'Guest',
  // Derived labels
  dueTomorrow: 'Tomorrow',
  dueThisWeek: 'This week',
  dueLater: 'Upcoming',
  dueNoDate: 'No deadline',
  projects: 'Projects',
  carriedOverCount: 'Carried over ({count})',
  privacyOnlyYou: 'Only you can see this',
  privacyOwnersCantSee: "Owners can't see anyone's My Work.",
  privacyMarkedDoneInPlan: 'Marked done in your plan; the team status is unchanged',
  privacySentForReview: 'Sent for review',
  visibleTo: 'Visible to {team}',
  createForTeam: 'Create in {projectKey} for the team',
} as const);
